import { sportOrthoSchedulingConfig } from "@/configuration/groups/sportortho/scheduling/config";
import { logAndThrow } from "@/src/lib/logging/logger";
import { HolidayNames } from "@/src/lib/utils/holidays";
import { AppointmentType } from "@/src/models/appointment";
import { Classification } from "@/src/models/constants/classifications";
import { ServiceLine } from "@/src/models/constants/service-lines";

const SPORTORTHO_SERVICE_LINES = [ServiceLine.ORTHOPEDICS, ServiceLine.PHYSICAL_THERAPY] as const;

const SPORTORTHO_HOLIDAYS = [
  HolidayNames.NEW_YEARS_DAY,
  HolidayNames.MEMORIAL_DAY,
  HolidayNames.INDEPENDENCE_DAY,
  HolidayNames.LABOR_DAY,
  HolidayNames.THANKSGIVING,
  HolidayNames.CHRISTMAS_DAY,
] as const;

type SportOrthoServiceLine = (typeof SPORTORTHO_SERVICE_LINES)[number];

export function getSportOrthoServiceLines(): SportOrthoServiceLine[] {
  return [...SPORTORTHO_SERVICE_LINES];
}

export function getSportOrthoHolidays(): HolidayNames[] {
  return [...SPORTORTHO_HOLIDAYS];
}

/**
 * Brentwood, the only clinic the Voice line schedules for. Every escalation extension routes to
 * the Brentwood branch and the FAQ is written around it, so availability is scoped here too (see
 * configuration/groups/sportortho/voice-sites.ts).
 */
export const SPORT_ORTHO_BRENTWOOD_DEPARTMENT_ID = "3";

// Athena department IDs mapped to the customer-facing location names from faq.md
export const SPORTORTHO_DEPARTMENT_NAMES: Record<string, string> = {
  "2": "Antioch",
  [SPORT_ORTHO_BRENTWOOD_DEPARTMENT_ID]: "Brentwood",
  "5": "Clarksville",
  "8": "Hendersonville",
  "11": "Lawrenceburg",
  "12": "Manchester",
  "13": "Mt. Juliet",
  "15": "Murfreesboro S",
  "17": "East Nashville",
  "18": "Murfreesboro N",
  "21": "Spring Hill",
  "23": "White House",
};

export function getSportOrthoDepartmentIds(): string[] {
  return Object.keys(SPORTORTHO_DEPARTMENT_NAMES);
}

/**
 * Every clinic except Brentwood, for appointment-history reads only.
 *
 * The Voice line books exclusively at Brentwood, but surgery and fracture risk follows the
 * patient, not the clinic: a rotator cuff repair at Antioch is the same reason to route a caller
 * to staff as one at Brentwood. Widening only the history read keeps availability scoped (see
 * configuration/groups/sportortho/voice-sites.ts).
 */
export const SPORT_ORTHO_RELATED_HISTORY_DEPARTMENT_IDS = getSportOrthoDepartmentIds().filter(
  (departmentId) => departmentId !== SPORT_ORTHO_BRENTWOOD_DEPARTMENT_ID
);

export function getSportOrthoDepartmentName(departmentId: string): string | null {
  return SPORTORTHO_DEPARTMENT_NAMES[departmentId] ?? null;
}

export function getSportOrthoIntakeClassification(classification: Classification): Classification {
  return classification;
}

export function getSportOrthoClassificationToExtract(): Classification[] {
  return [Classification.REFERRAL];
}

export function getSportOrthoUploadClassification(classification: Classification): Classification {
  return classification;
}

export function getSportOrthoScheduleConfigPath(): string {
  return sportOrthoSchedulingConfig.policy.scheduleConfigPath;
}

// Appointment-type-ID config, derived from real Sport Ortho open + booked appointment
// data (Brentwood dept 3, East Nashville dept 17). Every bookable ortho provider books from one open
// template — "Any" (502) — into one of three booked types by intent, so the mapping is uniform across
// providers and defined once. Clinic categories map to: New Patient -> NEW_PATIENT_CHECKIN,
// Established -> EXISTING_PATIENT_CHECKIN, Follow Up -> FOLLOW_UP.

interface ProviderAppointmentTypeMapping {
  appointmentTypeIds: Partial<Record<AppointmentType, number>>;
  defaultAppointmentTypeId?: number;
}

enum SportOrthoOpenAppointmentTypeId {
  ANY = 502,
}

enum SportOrthoBookedAppointmentTypeId {
  FOLLOW_UP = 706,
  NEW_PATIENT = 329,
  ESTABLISHED_PATIENT = 96,
  POST_OP_EXAM = 841,
}

interface OpenSlotMapping {
  default: SportOrthoBookedAppointmentTypeId;
  byIntent?: Partial<Record<AppointmentType, SportOrthoBookedAppointmentTypeId>>;
}

type OpenToBookedTypeIdMapping = Partial<Record<SportOrthoOpenAppointmentTypeId, OpenSlotMapping>>;

// Athena scheduling EHR ids (Receiving Provider `ehr_id`) for every bookable ortho provider. All
// ortho providers book from the same "Any" (502) open template, so the mapping below is uniform —
// listing a provider's id here is all that's needed to make them bookable. Keep in sync with the
// enabled providers in rules/schedule.yaml. PT and excluded providers are intentionally absent.
//   198 Meador · 63 McCool · 418 Flanagan · 332 Dahir · 20 Hensley · 25 Cosminsky · 3 Kaelin · 353 Mitchell
//   317 Guisinger · 316 Gilmer · 345 Nava · 401 Emholz · 415 Walker · 350 Rice · 421 Dantzker · 352 Metzger
//   251 Johnson · 267 Hood · 435 Baker · 29 Bell · 47 Thomas · 454 Camino
const SPORT_ORTHO_BOOKABLE_PROVIDER_EHR_IDS = [
  "198",
  "267",
  "63",
  "418",
  "435",
  "332",
  "20",
  "25",
  "3",
  "353",
  "317",
  "316",
  "345",
  "401",
  "415",
  "350",
  "421",
  "352",
  "251",
  "29",
  "47",
  "454",
] as const;

const ORTHO_OPEN_TO_BOOKED: OpenToBookedTypeIdMapping = {
  [SportOrthoOpenAppointmentTypeId.ANY]: {
    // Follow Up is the dominant booked type for the "Any" template.
    default: SportOrthoBookedAppointmentTypeId.FOLLOW_UP,
    byIntent: {
      [AppointmentType.NEW_PATIENT_CHECKIN]: SportOrthoBookedAppointmentTypeId.NEW_PATIENT,
      [AppointmentType.INITIAL_CONSULTATION]: SportOrthoBookedAppointmentTypeId.NEW_PATIENT,
      [AppointmentType.EXISTING_PATIENT_CHECKIN]: SportOrthoBookedAppointmentTypeId.ESTABLISHED_PATIENT,
    },
  },
};

const ORTHO_OPEN_APPOINTMENT_TYPE: ProviderAppointmentTypeMapping = {
  appointmentTypeIds: {
    [AppointmentType.NEW_PATIENT_CHECKIN]: SportOrthoOpenAppointmentTypeId.ANY,
    [AppointmentType.INITIAL_CONSULTATION]: SportOrthoOpenAppointmentTypeId.ANY,
    [AppointmentType.EXISTING_PATIENT_CHECKIN]: SportOrthoOpenAppointmentTypeId.ANY,
    [AppointmentType.FOLLOW_UP]: SportOrthoOpenAppointmentTypeId.ANY,
  },
  defaultAppointmentTypeId: SportOrthoOpenAppointmentTypeId.ANY,
};

const ORTHO_BOOKED_APPOINTMENT_TYPE: ProviderAppointmentTypeMapping = {
  appointmentTypeIds: {
    [AppointmentType.NEW_PATIENT_CHECKIN]: SportOrthoBookedAppointmentTypeId.NEW_PATIENT,
    [AppointmentType.INITIAL_CONSULTATION]: SportOrthoBookedAppointmentTypeId.NEW_PATIENT,
    [AppointmentType.EXISTING_PATIENT_CHECKIN]: SportOrthoBookedAppointmentTypeId.ESTABLISHED_PATIENT,
    [AppointmentType.FOLLOW_UP]: SportOrthoBookedAppointmentTypeId.FOLLOW_UP,
  },
};

const ORTHO_BOOKED_REVERSE: Partial<Record<SportOrthoBookedAppointmentTypeId, AppointmentType>> = {
  [SportOrthoBookedAppointmentTypeId.FOLLOW_UP]: AppointmentType.FOLLOW_UP,
  [SportOrthoBookedAppointmentTypeId.NEW_PATIENT]: AppointmentType.INITIAL_CONSULTATION,
  [SportOrthoBookedAppointmentTypeId.ESTABLISHED_PATIENT]: AppointmentType.FOLLOW_UP,
  // Post Op Exam is a follow-up-style visit; label it as FOLLOW_UP.
  [SportOrthoBookedAppointmentTypeId.POST_OP_EXAM]: AppointmentType.FOLLOW_UP,
};

function byBookableProvider<T>(value: T): Record<string, T> {
  return Object.fromEntries(SPORT_ORTHO_BOOKABLE_PROVIDER_EHR_IDS.map((ehrId) => [ehrId, value]));
}

const OPEN_TYPE_ID_TO_BOOKING_TYPE_ID_MAPPING: Record<string, OpenToBookedTypeIdMapping> =
  byBookableProvider(ORTHO_OPEN_TO_BOOKED);

const OPEN_APPOINTMENT_TYPE_MAPPING: Record<string, ProviderAppointmentTypeMapping> =
  byBookableProvider(ORTHO_OPEN_APPOINTMENT_TYPE);

const BOOKED_APPOINTMENT_TYPE_MAPPING: Record<string, ProviderAppointmentTypeMapping> =
  byBookableProvider(ORTHO_BOOKED_APPOINTMENT_TYPE);

function getSportOrthoMapping(
  providerEhrId: string,
  mapping: Record<string, ProviderAppointmentTypeMapping>
): ProviderAppointmentTypeMapping {
  const providerMapping = mapping[providerEhrId];

  if (!providerMapping) {
    logAndThrow("No appointment type mapping for Sport Ortho provider EHR ID", { providerEhrId });
  }

  return providerMapping;
}

export function getSportOrthoOpenAppointmentTypeId(providerEhrId: string, appointmentType: AppointmentType): number {
  const mapping = getSportOrthoMapping(providerEhrId, OPEN_APPOINTMENT_TYPE_MAPPING);
  const typeId = mapping.appointmentTypeIds[appointmentType] ?? mapping.defaultAppointmentTypeId;

  if (typeId === undefined) {
    logAndThrow("No open appointment type mapping for Sport Ortho provider and appointment type", {
      providerEhrId,
      appointmentType,
    });
  }

  return typeId;
}

export function getSportOrthoBookedAppointmentTypeId(providerEhrId: string, appointmentType: AppointmentType): number {
  const mapping = getSportOrthoMapping(providerEhrId, BOOKED_APPOINTMENT_TYPE_MAPPING);
  const typeId = mapping.appointmentTypeIds[appointmentType] ?? mapping.defaultAppointmentTypeId;

  if (typeId === undefined) {
    logAndThrow("No booked appointment type mapping for Sport Ortho provider and appointment type", {
      providerEhrId,
      appointmentType,
    });
  }

  return typeId;
}

export function getSportOrthoBookedTypeIdFromOpenTypeId(
  providerEhrId: string,
  openAppointmentTypeId: string,
  internalAppointmentType: AppointmentType | null
): number {
  const providerMapping = OPEN_TYPE_ID_TO_BOOKING_TYPE_ID_MAPPING[providerEhrId];

  if (!providerMapping) {
    logAndThrow("No open->booked appointment type mapping for Sport Ortho provider EHR ID", { providerEhrId });
  }

  const slotMapping = providerMapping[Number(openAppointmentTypeId) as SportOrthoOpenAppointmentTypeId];

  if (slotMapping === undefined) {
    logAndThrow("No booked appointment type id for Sport Ortho provider + open type id", {
      providerEhrId,
      openAppointmentTypeId,
    });
  }

  if (internalAppointmentType && slotMapping.byIntent?.[internalAppointmentType] !== undefined) {
    return slotMapping.byIntent[internalAppointmentType] as number;
  }

  return slotMapping.default;
}

// Provider-agnostic: Athena appointment-type ids are practice-global. Returns null for types we
// don't model (e.g. Physical Therapy), so the caller falls back to keyword matching.
export function getSportOrthoAppointmentTypeFromBookedEhrId(
  _providerEhrId: string,
  athenaAppointmentTypeId: number
): AppointmentType | null {
  return ORTHO_BOOKED_REVERSE[athenaAppointmentTypeId as SportOrthoBookedAppointmentTypeId] ?? null;
}
